import { FC, useMemo, useState } from 'react';

import { InviteeSchema, UserRole } from 'types';
import { generateInvitationLink, generateInvitationText } from 'utils';
import { eventNameSelector } from 'store/selectors';
import { LoggerService } from 'services';
import { useStore } from 'hooks';
import { useLiveEvent } from 'containers/LiveEvent';
import { AnimatedContent, Loader } from 'components';

import GenerateLink from './InviteToStage.GenerateLink';
import ShareLink from './InviteToStage.ShareLink';

import styles from './InviteToStage.module.scss';

export type InviteToStageProps = {
  defaultInvitee?: InviteeSchema;
  defaultRole?: UserRole.SPEAKER | UserRole.MODERATOR;
  onGenerate?(name: string, role: UserRole.MODERATOR | UserRole.SPEAKER): Promise<InviteeSchema | null>;
  onReset?(inviteeCode: string): Promise<void>;
};

const InviteToStage: FC<InviteToStageProps> = ({
  defaultInvitee,
  defaultRole = UserRole.SPEAKER,
  onGenerate,
  onReset
}) => {
  const liveEvent = useLiveEvent();
  const eventName = useStore(eventNameSelector(liveEvent.eventCode));

  const [loading, setLoading] = useState<boolean>(false);
  const [invitee, setInvitee] = useState<InviteeSchema | null>(defaultInvitee || null);

  const activeContentIndex = useMemo(() => (invitee === null ? 0 : 1), [invitee]);

  const handleOnGenerate = async (inviteeName: string, role: UserRole.MODERATOR | UserRole.SPEAKER) => {
    setLoading(true);

    try {
      const createdInvitee = await onGenerate?.(inviteeName, role);

      setInvitee(createdInvitee || null);
    } catch (e) {
      LoggerService.log('GENERATE_INVITATION_LINK', e);
    } finally {
      setLoading(false);
    }
  };

  const handleOnReset = async () => {
    if (!invitee?.inviteeCode) return;

    setLoading(true);
    await onReset?.(invitee.inviteeCode);
    setLoading(false);
    setInvitee(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {loading && <Loader />}

        <AnimatedContent activeContentIndex={activeContentIndex}>
          <div className={styles.animated}>
            <GenerateLink defaultRole={defaultRole} onGenerate={handleOnGenerate} />
          </div>

          <div className={styles.animated}>
            <ShareLink
              text={invitee ? generateInvitationText(liveEvent.channelName, eventName, invitee) : ''}
              url={invitee ? generateInvitationLink(liveEvent.channelName, invitee) : ''}
              onReset={handleOnReset}
            />
          </div>
        </AnimatedContent>
      </div>
    </div>
  );
};

export default InviteToStage;
